import { Button, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { WaitingListItemData, WaitingListService, WaitingListStatus } from '../api';
import TitledDrawer from '../common/ui/TitledDrawer';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';

interface WaitingListUpdateDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  waitingListItem?: WaitingListItemData;
  onUpdate: (updatedItem: WaitingListItemData) => void;
}

export const WaitingListUpdateDialog = React.memo(
  ({ open, setOpen, waitingListItem, onUpdate }: WaitingListUpdateDialogProps) => {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);

    const initialValues = {
      status: waitingListItem?.status || 'ACTIVE',
      comment: waitingListItem?.comment || '',
    };

    const handleClose = () => {
      setOpen(false);
    };

    const onSubmit = async (values: typeof initialValues) => {
      if (!waitingListItem) return;

      setIsSaving(true);
      try {
        const updatedItem = await WaitingListService.updateWaitingListItem({
          id: waitingListItem.id,
          status: values.status as WaitingListStatus,
          comment: values.comment,
        });
        onUpdate(updatedItem);
        handleClose();
      } catch (error) {
        console.error('Failed to update waiting list item:', error);
      }
      setIsSaving(false);
    };

    return (
      <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
        {({ values, handleSubmit, handleChange, submitForm }) => (
          <Form onSubmit={handleSubmit}>
            <TitledDrawer onClose={handleClose} open={open} title={t('waitinglist.updateTitle')}>
              <Grid
                container
                flexDirection="column"
                gap={1.5}
                sx={{ pt: { xs: '12px', md: '18px' }, pb: 2, paddingInline: { xs: '12px', md: '24px' } }}>
                <FormControl fullWidth>
                  <InputLabel>{t('waitinglist.statuss')}</InputLabel>
                  <Select
                    style={{ width: 350 }}
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    label={t('waitinglist.statuss')}
                    disabled={values.status === WaitingListStatus.CONVERTED}>
                    <MenuItem value={WaitingListStatus.ACTIVE}>{t('waitinglist.status.active')}</MenuItem>
                    <MenuItem value={WaitingListStatus.PROCESSED}>{t('waitinglist.status.processed')}</MenuItem>
                    <MenuItem value={WaitingListStatus.CONVERTED}>{t('waitinglist.status.converted')}</MenuItem>
                  </Select>
                </FormControl>

                <FormTextField
                  width={350}
                  minRows={3}
                  fieldName="comment"
                  label={t('waitinglist.comment')}
                  onChange={handleChange}
                  disabled={false}
                  isFieldUpdate
                />

                <Grid container justifyContent="flex-end" gap={1}>
                  <Button onClick={handleClose}>{t('cancel')}</Button>
                  <LoadingButton loading={isSaving} variant="contained" onClick={submitForm}>
                    {t('save')}
                  </LoadingButton>
                </Grid>
              </Grid>
            </TitledDrawer>
          </Form>
        )}
      </Formik>
    );
  },
);
